import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import clsx from "clsx";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/EditOutlined";
import StorageIcon from "@material-ui/icons/StorageOutlined";
import AllIcon from "@material-ui/icons/AppsOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import { useProject } from "../../context/Project/ProjectContext";
import { useSortable } from "../../utils/useSortable";

const dataVal = (e, key) => {
  switch (key) {
    case "collection": return e.collection || "";
    case "key": return e.key || "";
    case "value": return typeof e.value === "string" ? e.value : JSON.stringify(e.value || "");
    default: return "";
  }
};

const useStyles = makeStyles(theme => ({
  page: { display: "flex", height: "100%", minHeight: 0, background: theme.app.chatBg },
  side: {
    width: 228,
    flexShrink: 0,
    borderRight: `1px solid ${theme.app.border}`,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  sideHead: { display: "flex", alignItems: "center", padding: theme.spacing(2, 2, 1) },
  sideTitle: { fontWeight: 600, fontSize: 13, color: theme.app.ink },
  sideCount: { marginLeft: "auto", fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textTertiary },
  list: { flex: 1, overflowY: "auto", padding: theme.spacing(0.5, 1) },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 10px",
    margin: "1px 0",
    borderRadius: 7,
    cursor: "pointer",
    color: theme.app.textSecondary,
    position: "relative",
    "&:hover": { background: theme.app.lineSoft },
  },
  itemActive: {
    background: "#F0F2F5",
    color: theme.app.ink,
    "&::before": { content: '""', position: "absolute", left: -4, top: 6, bottom: 6, width: 2.5, borderRadius: 2, background: theme.app.green },
  },
  itemIcon: { display: "flex", fontSize: 17, opacity: 0.75 },
  itemLabel: { flex: 1, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemCount: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textTertiary },
  sep: { margin: theme.spacing(0.75, 1.25), borderTop: `1px solid ${theme.app.lineSoft}` },
  main: { flex: 1, minWidth: 0, overflowY: "auto" },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(2.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  titleCount: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  grow: { flexGrow: 1 },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  headCell: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 600, borderBottom: `1px solid ${theme.app.border}`, padding: "0 16px", height: 38, lineHeight: "38px" },
  row: { "&:hover": { backgroundColor: theme.app.hover }, "&:hover $act": { visibility: "visible" } },
  cell: { borderBottom: `1px solid ${theme.app.lineSoft}`, padding: "0 16px", height: 46, fontSize: 13, color: theme.app.ink },
  keyCell: { fontWeight: 500, fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.ink },
  collCell: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  valCell: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textSecondary, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  act: { visibility: "hidden" },
  empty: { padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
  code: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.textSecondary },
}));

const preview = v => {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

const Data = () => {
  const { confirm } = useConfirm();
  const classes = useStyles();
  const menu = useMenuStyles();
  const { activeProjectId } = useProject();
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState("all"); // "all" | collection name
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, collection, key, text } | null
  const [editErr, setEditErr] = useState("");

  const fetchCollections = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/data/collections", { params: { projectId: activeProjectId } });
      setCollections(data.collections || []);
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  const fetchEntries = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const params = { projectId: activeProjectId };
      if (selected !== "all") params.collection = selected;
      const { data } = await api.get("/data/entries", { params });
      setEntries(data.entries || []);
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId, selected]);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const totalCount = collections.reduce((s, c) => s + Number(c.count || 0), 0);

  const openEdit = e => setEditing({ id: e.id, collection: e.collection, key: e.key, text: JSON.stringify(e.value, null, 2) });
  const openNew = () =>
    setEditing({ collection: selected !== "all" ? selected : "default", key: "", text: '""' });

  const saveEdit = async () => {
    let value;
    try {
      value = JSON.parse(editing.text);
    } catch {
      setEditErr("JSON inválido.");
      return;
    }
    try {
      if (editing.id) {
        await api.put(`/data/entries/${editing.id}`, { value });
      } else {
        if (!editing.key.trim()) { setEditErr("Informe a chave."); return; }
        await api.post("/data/entries", {
          projectId: activeProjectId,
          collection: editing.collection || "default",
          key: editing.key.trim(),
          value,
        });
      }
      setEditing(null);
      setEditErr("");
      fetchCollections();
      fetchEntries();
    } catch (err) {
      toastError(err);
    }
  };

  const removeEntry = async e => {
    if (!(await confirm({ title: "Excluir dado", message: `Excluir "${e.collection}/${e.key}"?`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/data/entries/${e.id}`);
      fetchCollections();
      fetchEntries();
    } catch (err) {
      toastError(err);
    }
  };

  const { sorted, sort, toggle } = useSortable(entries, dataVal);
  const th = (key, label) => (
    <TableCell className={classes.headCell} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : "asc"} onClick={() => toggle(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <div className={classes.page}>
      {/* collections rail */}
      <div className={classes.side}>
        <div className={classes.sideHead}>
          <Typography className={classes.sideTitle}>Coleções</Typography>
          <span className={classes.sideCount}>{collections.length}</span>
        </div>
        <div className={classes.list}>
          <div className={clsx(classes.item, selected === "all" && classes.itemActive)} onClick={() => setSelected("all")}>
            <span className={classes.itemIcon}><AllIcon fontSize="inherit" /></span>
            <span className={classes.itemLabel}>Tudo</span>
            <span className={classes.itemCount}>{totalCount}</span>
          </div>
          <div className={classes.sep} />
          {collections.map(c => (
            <div key={c.collection} className={clsx(classes.item, selected === c.collection && classes.itemActive)} onClick={() => setSelected(c.collection)}>
              <span className={classes.itemIcon}><StorageIcon fontSize="inherit" /></span>
              <span className={classes.itemLabel}>{c.collection}</span>
              <span className={classes.itemCount}>{c.count}</span>
            </div>
          ))}
          {collections.length === 0 && (
            <div className={classes.empty} style={{ padding: 16, fontSize: 13 }}>
              Sem dados ainda. Use o nó "Salvar dado" num fluxo, ou crie aqui.
            </div>
          )}
        </div>
      </div>

      {/* entries */}
      <div className={classes.main}>
        <div className={classes.wrap}>
          <div className={classes.header}>
            <Typography className={classes.title}>{selected === "all" ? "Todos os dados" : selected}</Typography>
            <span className={classes.titleCount}>{entries.length}</span>
            <div className={classes.grow} />
            <Button variant="contained" color="secondary" disableElevation startIcon={<AddIcon />} disabled={!activeProjectId} onClick={openNew}>
              Novo registro
            </Button>
          </div>

          <div className={classes.tbl}>
            <Table>
              <TableHead>
                <TableRow>
                  {th("collection", "Coleção")}
                  {th("key", "Chave")}
                  {th("value", "Valor")}
                  <TableCell align="right" className={classes.headCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(e => (
                  <TableRow key={e.id} className={classes.row}>
                    <TableCell className={clsx(classes.cell, classes.collCell)}>{e.collection}</TableCell>
                    <TableCell className={clsx(classes.cell, classes.keyCell)}>{e.key}</TableCell>
                    <TableCell className={clsx(classes.cell, classes.valCell)}>{preview(e.value)}</TableCell>
                    <TableCell align="right" className={classes.cell}>
                      <span className={classes.act}>
                        <IconButton size="small" onClick={() => openEdit(e)} title="Editar">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => removeEntry(e)} title="Excluir">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {entries.length === 0 && <div className={classes.empty}>Nenhum registro.</div>}
          </div>
        </div>
      </div>

      <Dialog open={!!editing} onClose={() => { setEditing(null); setEditErr(""); }} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>{editing && editing.id ? "Editar registro" : "Novo registro"}</DialogTitle>
        <DialogContent>
          {editing && !editing.id && (
            <>
              <TextField
                label="Coleção"
                value={editing.collection}
                onChange={e => setEditing({ ...editing, collection: e.target.value })}
                fullWidth variant="outlined" size="small" margin="dense"
              />
              <TextField
                label="Chave"
                value={editing.key}
                onChange={e => setEditing({ ...editing, key: e.target.value })}
                fullWidth variant="outlined" size="small" margin="dense"
              />
            </>
          )}
          {editing && editing.id && (
            <Typography className={classes.code} style={{ marginBottom: 8 }}>
              {editing.collection} / {editing.key}
            </Typography>
          )}
          <TextField
            label="Valor (JSON)"
            value={editing ? editing.text : ""}
            onChange={e => { setEditing({ ...editing, text: e.target.value }); if (editErr) setEditErr(""); }}
            fullWidth multiline rows={10} variant="outlined" size="small" margin="dense"
            error={!!editErr} helperText={editErr || " "}
            InputProps={{ style: { fontFamily: "monospace", fontSize: 12.5 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditing(null); setEditErr(""); }}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={saveEdit}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Data;
